import axios, { InternalAxiosRequestConfig } from 'axios';
import { authManager } from './authManager';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

// Create axios instance
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track if we're currently refreshing to prevent loops
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

function subscribeToRefresh(callback: (token: string) => void) {
  refreshSubscribers.push(callback);
}

function notifyRefreshSubscribers(token: string) {
  refreshSubscribers.forEach(callback => callback(token));
  refreshSubscribers = [];
}

// Request interceptor to add auth token
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Skip token for auth endpoints that don't need it
    const noAuthEndpoints = [
      '/auth/login',
      '/auth/register',
      '/auth/forgot-password',
      '/auth/reset-password',
      '/auth/verify-email',
      '/auth/token/refresh',
      '/auth/exchange-code',
      '/oauth/config',
      '/oauth/google',
    ];

    const needsAuth = !noAuthEndpoints.some(endpoint =>
      config.url?.includes(endpoint)
    );

    if (needsAuth) {
      if (navigator.onLine) {
        // Online: get a valid access token (will auto-refresh if needed)
        const token = await authManager.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } else {
        // Offline: use cached token, skip refresh attempt that would fail and logout
        const token = authManager.getCurrentToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
    }

    // Add cache-busting headers for mobile devices
    config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
    config.headers['Pragma'] = 'no-cache';
    config.headers['Expires'] = '0';

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Auth endpoints where 401 should be handled by the caller, not the interceptor
const authEndpoints = [
  '/auth/login',
  '/auth/register',
  '/auth/token/refresh',
  '/auth/exchange-code',
  '/oauth/',
];

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // 402 errors (subscription tier gating) — silently reject, callers handle gracefully
    if (error.response?.status === 402) {
      return Promise.reject(error);
    }

    // Don't intercept 401s from auth endpoints - let the caller handle them
    const isAuthEndpoint = authEndpoints.some(endpoint =>
      originalRequest.url?.includes(endpoint)
    );

    // Handle 401 errors (unauthorized)
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      // Don't attempt refresh when offline — would fail and clear session
      if (!navigator.onLine) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      // If we're already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeToRefresh((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(api(originalRequest));
          });
        });
      }

      isRefreshing = true;

      try {
        // Try to get a fresh token
        const token = await authManager.getAccessToken();

        if (token) {
          isRefreshing = false;
          notifyRefreshSubscribers(token);

          // Retry the original request
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }
      } catch {
        // Token refresh failed
      }

      isRefreshing = false;

      // If refresh failed, logout and redirect
      await authManager.logout();

      // Redirect to login page, preserving the current location so the user
      // returns to where they were after logging back in
      if (window.location.pathname !== '/login') {
        const currentPath = window.location.pathname + window.location.search;
        const returnTo = currentPath !== '/' ? `?returnTo=${encodeURIComponent(currentPath)}` : '';
        window.location.href = `/login${returnTo}`;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
